/**
 * Surface picking via Three.js raycaster.
 *
 * Port of ui/src/viewer/picking.js: takes screen-space (clientX, clientY),
 * builds a ray through the camera, intersects against the scene, returns
 * the first hit world-space point or null.
 *
 * Two-stage raycast:
 *   1. The live tile group — whatever the renderer is currently displaying.
 *      This is the normal path and naturally ignores hidden LOD parents
 *      (the renderer removes them from the group).
 *   2. Fallback: tile content that is loaded but the renderer has *culled
 *      out of the group* (`setTileVisible(tile,false)` → `group.remove`).
 *      A small single-tile model gets frustum-culled — and oscillates in and
 *      out of the group — the instant you zoom in close, so stage 1 often
 *      finds nothing. Stage 2 keeps a stable reference to the loaded geometry
 *      so you can still measure it. Mirrors the desktop viewer, which
 *      raycasts a stable full-resolution mesh.
 */

import {
  Camera,
  Intersection,
  Object3D,
  Raycaster,
  Vector2,
  Vector3,
} from 'three'

export class Picking {
  private raycaster = new Raycaster()
  private ndc = new Vector2()

  constructor(
    private canvas: HTMLCanvasElement,
    private camera: Camera,
    private root: Object3D,
    /** Loaded-but-maybe-hidden tile scenes, raycast only when the live group
     * yields nothing under the cursor. */
    private getFallbackTargets: () => Object3D[] = () => [],
  ) {}

  /** Convert (clientX, clientY) → world-space Vector3 on the first hit, or null. */
  pickSurface(clientX: number, clientY: number): Vector3 | null {
    const rect = this.canvas.getBoundingClientRect()
    this.ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1
    this.ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1
    this.raycaster.setFromCamera(this.ndc, this.camera)

    // Stage 1 — currently displayed tiles. recursive=true walks into the
    // 3d-tiles renderer's tile group.
    const groupPick = this.firstAccepted(this.raycaster.intersectObject(this.root, true))
    if (groupPick) return groupPick

    // Stage 2 — geometry the renderer culled out of the group.
    const fallback = this.getFallbackTargets()
    if (fallback.length) {
      const fbPick = this.firstAccepted(this.raycaster.intersectObjects(fallback, true))
      if (fbPick) return fbPick
    }

    return null
  }

  /** Walk the sorted hits, skip overlay materials (markers, gizmos — they use
   * depthTest:false), return the first real surface point (cloned). */
  private firstAccepted(hits: Intersection[]): Vector3 | null {
    for (const hit of hits) {
      const mat = (hit.object as { material?: { depthTest?: boolean } }).material
      if (mat && mat.depthTest === false) continue
      return hit.point.clone()
    }
    return null
  }
}
